import React from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { UserIcon, Key, FileText, Activity, TestTube2, Shield } from 'lucide-react';
import ManageTherapists from './ManageTherapists';
import APIKeyManagement from './APIKeyManagement';
import DocumentExtractionManagement from './DocumentExtractionManagement';
import APITestSuite from './APITestSuite';
import SecurityAlerts from './SecurityAlerts';

const AdminHome = () => {
  return (
    <div className="p-4">
      <h1 className="text-xl font-semibold text-slate-900">Administration</h1>
      <p className="mt-1 text-sm text-slate-500">Access administrative tools and settings</p>
      
      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {/* Manage Therapists Card */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserIcon className="h-6 w-6 text-slate-400" aria-hidden="true" />
              </div>
              <div className="ml-5">
                <h3 className="text-lg font-medium text-slate-900">Manage Therapists</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Add, edit, or manage therapist accounts
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-50 px-5 py-3">
            <div className="text-sm">
              <Link 
                to="therapists"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                View section
              </Link>
            </div>
          </div>
        </div>

        {/* API Key Management Card */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Key className="h-6 w-6 text-slate-400" aria-hidden="true" />
              </div>
              <div className="ml-5">
                <h3 className="text-lg font-medium text-slate-900">API Key Management</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Generate and manage API keys for integrations
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-50 px-5 py-3">
            <div className="text-sm">
              <Link 
                to="api-key"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                View section
              </Link>
            </div>
          </div>
        </div>

        {/* Document Extraction Management Card */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="h-6 w-6 text-slate-400" aria-hidden="true" />
              </div>
              <div className="ml-5">
                <h3 className="text-lg font-medium text-slate-900">Document Extraction</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Monitor and manage automatic document processing
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-50 px-5 py-3">
            <div className="text-sm">
              <Link 
                to="document-extraction"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                View section
              </Link>
            </div>
          </div>
        </div>

        {/* System Activity Card */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-6 w-6 text-slate-400" aria-hidden="true" />
              </div>
              <div className="ml-5">
                <h3 className="text-lg font-medium text-slate-900">System Activity</h3>
                <p className="mt-1 text-sm text-slate-500">
                  View system logs and security alerts
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-50 px-5 py-3">
            <div className="text-sm">
              <Link 
                to="activity"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                View section
              </Link>
            </div>
          </div>
        </div>

        {/* Security Alerts Card */}
        <div className="bg-white overflow-hidden shadow rounded-lg border border-red-100">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Shield className="h-6 w-6 text-red-500" aria-hidden="true" />
              </div>
              <div className="ml-5">
                <h3 className="text-lg font-medium text-slate-900">Security Alerts</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Review and manage system security events
                </p>
              </div>
            </div>
          </div>
          <div className="bg-red-50 px-5 py-3">
            <div className="text-sm">
              <Link 
                to="security"
                className="font-medium text-red-600 hover:text-red-500"
              >
                View alerts
              </Link>
            </div>
          </div>
        </div>

        {/* API Test Suite Card */}
        <div className="bg-white overflow-hidden shadow rounded-lg border-2 border-blue-200">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TestTube2 className="h-6 w-6 text-blue-500" aria-hidden="true" />
              </div>
              <div className="ml-5">
                <h3 className="text-lg font-medium text-slate-900">API Test Suite</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Test all endpoints for functionality and security
                </p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 px-5 py-3">
            <div className="text-sm">
              <Link 
                to="api-tests"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Run tests
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Section */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-slate-900 mb-4">Quick Overview</h2>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="text-center">
              <div className="text-2xl font-semibold text-blue-600">
                <UserIcon className="h-8 w-8 mx-auto mb-2" />
              </div>
              <p className="text-sm text-slate-500">Active Therapists</p>
              <p className="text-lg font-medium text-slate-900">View Details</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-green-600">
                <FileText className="h-8 w-8 mx-auto mb-2" />
              </div>
              <p className="text-sm text-slate-500">Documents Processed</p>
              <p className="text-lg font-medium text-slate-900">View Logs</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-orange-600">
                <Activity className="h-8 w-8 mx-auto mb-2" />
              </div>
              <p className="text-sm text-slate-500">System Status</p>
              <p className="text-lg font-medium text-slate-900">All Systems Operational</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminSection = () => {
  return (
    <Routes>
      <Route index element={<AdminHome />} />
      <Route path="therapists" element={<ManageTherapists />} />
      <Route path="api-key" element={<APIKeyManagement />} />
      <Route path="document-extraction" element={<DocumentExtractionManagement />} />
      <Route path="api-tests" element={<APITestSuite />} />
      <Route path="security" element={<SecurityAlerts />} />
      {/* Add more admin routes as needed */}
    </Routes>
  );
};

export default AdminSection;